import { Box, Skeleton, Tab } from '@mui/material';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CerebroBreadcrumb, CerebroPageContainer, CerebroSecondaryButton } from '../../../shared/components';
import { useAuth } from '../../../shared/context';
import { useDataRoomQuery, useDataRoomBorrowerParticipantsQuery, useDealQuery } from '../../../shared/hooks';
import { CompanyContactDataRoom } from '../../../shared/models';
import { SendAccountInfoForm } from './SendAccountInfoForm';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { BorrowerList } from './BorrowerList';
import { CompanyContact } from '../../../shared/models/CompanyContact';
import { AvailableBorrowerList } from './AvailableBorrowerList';
import { AddColleagueDrawer } from './AddColleagueDrawer';
import { AddColleagueAccessDrawer } from './AddColleagueAccessDrawer';
import { EditColleagueAccessDrawer } from './EditColleagueAccessDrawer';
import { useDataRoomContactsQuery } from '../../../shared/hooks/Queries/useDataRoomContactsQuery';

export const BorrowerParticipants: React.FC = () => {
	const { currentUser } = useAuth();
	const { guid } = useParams();
	const dataRoomQuery = useDataRoomQuery(guid);
	const dealQuery = useDealQuery(dataRoomQuery.data?.dealGuid ?? '', {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});
	const dataRoomContactsQuery = useDataRoomContactsQuery(guid);
	const contactsQuery = useDataRoomBorrowerParticipantsQuery(guid);
	const companyGuid = dealQuery.data?.companyGuid;
	const isLoading =
		dataRoomQuery.isLoading || contactsQuery.isLoading || dataRoomContactsQuery.isLoading || dealQuery.isLoading;

	const [tab, setTab] = useState('full');
	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	const [sendAccountInfo, setSendAccountInfo] = React.useState<CompanyContactDataRoom | null>(null);
	const [editAccess, setEditAccess] = useState<CompanyContactDataRoom | null>(null);
	const [addAccess, setAddAccess] = useState<CompanyContact | null>(null);
	const [addColleague, setAddColleague] = useState(false);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Participants &gt; Borrower Contacts</CerebroBreadcrumb>

			{isLoading && (
				<>
					<Skeleton height={200} />
					<Skeleton height={200} />
					<Skeleton height={200} />
				</>
			)}

			{contactsQuery.isSuccess &&
				dataRoomQuery.isSuccess &&
				dataRoomContactsQuery.isSuccess &&
				dealQuery.isSuccess && (
					<>
						<Box sx={{ marginBottom: 3 }}>
							<CerebroSecondaryButton sx={{ marginRight: 1 }} onClick={() => setAddColleague(true)}>
								Add/Edit Access
							</CerebroSecondaryButton>
						</Box>
						<TabContext value={tab}>
							<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
								<TabList onChange={handleTabChange}>
									<Tab label="Full Access" value="full" />
									<Tab label="Limited Access" value="limited" />
									<Tab label="3rd Party" value="3rd" />
									<Tab label="None" value="none" />
								</TabList>
							</Box>
							<TabPanel value="full">
								<BorrowerList
									currentUser={currentUser}
									hasFullAccess={contactsQuery.data.hasFullAccess}
									dataRoom={dataRoomQuery.data}
									borrowers={contactsQuery.data.dataRoomContacts.filter(
										w => w.accessType == 'FullAccess'
									)}
									onEdit={setEditAccess}
									onSendAccountInfo={setSendAccountInfo}
								/>
							</TabPanel>
							<TabPanel value="limited">
								<BorrowerList
									currentUser={currentUser}
									hasFullAccess={contactsQuery.data.hasFullAccess}
									dataRoom={dataRoomQuery.data}
									borrowers={contactsQuery.data.dataRoomContacts.filter(
										w => w.accessType == 'LimitedAccess'
									)}
									onEdit={setEditAccess}
									onSendAccountInfo={setSendAccountInfo}
								/>
							</TabPanel>
							<TabPanel value="3rd">
								<BorrowerList
									currentUser={currentUser}
									hasFullAccess={contactsQuery.data.hasFullAccess}
									dataRoom={dataRoomQuery.data}
									borrowers={contactsQuery.data.dataRoomContacts.filter(
										w => w.isThirdPartyContact == true
									)}
									onEdit={setEditAccess}
									onSendAccountInfo={setSendAccountInfo}
								/>
							</TabPanel>
							<TabPanel value="none">
								<AvailableBorrowerList
									currentUser={currentUser}
									hasFullAccess={contactsQuery.data.hasFullAccess}
									borrowers={contactsQuery.data.availableContacts}
									onAdd={setAddAccess}
								/>
							</TabPanel>
						</TabContext>

						<AddColleagueDrawer
							open={addColleague}
							isContactParentCompany={
								contactsQuery.data.isContactParentCompany || currentUser.isEmployeeUser
							}
							companies={contactsQuery.data.companies}
							companyGuid={companyGuid}
							dataRoomGuid={guid}
							currentUser={currentUser}
							onClose={() => setAddColleague(false)}
						/>

						<AddColleagueAccessDrawer
							record={addAccess}
							companyGuid={companyGuid}
							dataRoomGuid={guid}
							currentUser={currentUser}
							onClose={() => setAddAccess(null)}
						/>

						<EditColleagueAccessDrawer
							record={editAccess}
							companyGuid={companyGuid}
							dataRoomGuid={guid}
							currentUser={currentUser}
							onClose={() => setEditAccess(null)}
						/>

						<SendAccountInfoForm
							dataRoomGuid={guid}
							contact={sendAccountInfo}
							close={() => setSendAccountInfo(null)}
						/>
					</>
				)}
		</CerebroPageContainer>
	);
};

export function getAccessStatus(record: CompanyContactDataRoom) {
	if (record.accessType === 'FullAccess') {
		return 'Full Access';
	} else if (record.accessType === 'LimitedAccess') {
		return 'Limited Access';
	}

	return 'No Access';
}
